import { useCallback, useEffect, useRef } from "react";

const SCROLLBAR_HIDING_STYLE =
  "<style>html{scrollbar-width:none}html::-webkit-scrollbar{display:none}body{background-color:white}</style>";

/**
 * Prepends scrollbar-hiding styles to HTML so they apply from first paint,
 * avoiding a flash of scrollbar before the iframe load event fires.
 */
export function wrapHtmlForScrollingFrame(html: string): string {
  if (!html) return "";
  if (html.includes("</head>")) {
    return html.replace("</head>", `${SCROLLBAR_HIDING_STYLE}</head>`);
  }
  return SCROLLBAR_HIDING_STYLE + html;
}

const SCROLL_PIXELS_PER_SECOND = 60;

export function useScrollingFrame() {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const rafId = useRef<number | null>(null);

  const stopScrolling = useCallback(() => {
    if (rafId.current == null) return;
    cancelAnimationFrame(rafId.current);
    rafId.current = null;
  }, []);

  // Cancel the animation frame on unmount. Virtualization can remove a hovered
  // card before mouseleave fires, which would otherwise leave the loop running
  // forever against a detached iframe.
  useEffect(() => stopScrolling, [stopScrolling]);

  const handleMouseEnter = useCallback(() => {
    stopScrolling();
    let direction = 1;
    let position = iframeRef.current?.contentWindow?.scrollY ?? 0;
    let lastTime: number | null = null;
    const step = (time: number) => {
      const iframe = iframeRef.current;
      const win = iframe?.contentWindow;
      const doc = win?.document;
      if (iframe && win && doc?.body) {
        const maxScroll = doc.body.scrollHeight - iframe.clientHeight;
        // Non-scrollable content: nothing to animate, so stop instead of spinning every frame.
        if (maxScroll <= 0) {
          rafId.current = null;
          return;
        }
        const deltaMs = lastTime == null ? 0 : time - lastTime;
        position += direction * SCROLL_PIXELS_PER_SECOND * (deltaMs / 1000);
        if (position >= maxScroll) {
          position = maxScroll;
          direction = -1;
        } else if (position <= 0) {
          position = 0;
          direction = 1;
        }
        win.scrollTo(0, position);
      }
      lastTime = time;
      rafId.current = requestAnimationFrame(step);
    };
    rafId.current = requestAnimationFrame(step);
  }, [stopScrolling]);

  const handleMouseLeave = useCallback(() => {
    stopScrolling();
    // Optionally reset scroll position
    if (iframeRef.current && iframeRef.current.contentWindow) {
      iframeRef.current.contentWindow.scrollTo(0, 0);
    }
  }, [stopScrolling]);

  const handleIframeLoad = useCallback(() => {
    // No-op: scrollbar styles are injected via wrapHtmlForScrollingFrame for first-paint.
    // Kept for future use if we need to hook into the iframe load event.
  }, []);

  return {
    iframeRef,
    handleMouseEnter,
    handleMouseLeave,
    handleIframeLoad,
  };
}
